import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AccountingVouchersService } from "services/accounting-vouchers";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createLine = () => ({
  description: "",
  debitAccount: "",
  creditAccount: "",
  amount: "",
  note: "",
  origin: "manual",
  sourceType: "",
  sourceId: "",
});

const createInitialForm = () => ({
  unit: "",
  companyAddress: "",
  voucherNumber: "",
  voucherDate: "",
  description: "",
  numberingMode: "MONTH",
  attachmentsCount: "",
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  signDate: "",
  sourceRefs: [],
  status: "DRAFT",
  lines: [createLine()],
});

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) {
    throw new Error(`${label} không được để trống`);
  }
  if (!hasAllDatePart(parts)) {
    throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  }

  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);

  if (month < 1 || month > 12) {
    throw new Error(`${label} có tháng không hợp lệ`);
  }
  if (year < 1900 || year > 9999) {
    throw new Error(`${label} có năm không hợp lệ`);
  }

  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) {
    throw new Error(`${label} có ngày không hợp lệ`);
  }

  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const sanitizeDatePart = (value, maxLength) => value.replace(/\D/g, "").slice(0, maxLength);
const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};
const fmt = (n) => new Intl.NumberFormat("vi-VN").format(roundMoney(n));

const getTodayDateParts = () => {
  const d = new Date();
  return {
    day: String(d.getDate()).padStart(2, "0"),
    month: String(d.getMonth() + 1).padStart(2, "0"),
    year: String(d.getFullYear()),
  };
};

const sourceTypeOptions = [
  { label: "Phiếu thu", value: "cash-receipt" },
  { label: "Phiếu chi", value: "cash-voucher" },
  { label: "Phiếu nhập kho", value: "goods-received-note" },
  { label: "Phiếu xuất kho", value: "goods-delivery-note" },
  { label: "Giấy thanh toán tạm ứng", value: "advance-payment-request" },
  { label: "Bảng kê mua hàng", value: "purchase-order" },
  { label: "Bảng thanh toán ký gửi", value: "consignment-settlement" },
  { label: "Biên bản giao nhận TSCĐ", value: "asset-handover-record" },
  { label: "Biên bản thanh lý TSCĐ", value: "fixed-asset-liquidation" },
  {
    label: "Biên bản bàn giao TSCĐ sửa chữa",
    value: "handover-record-ruf-assets",
  },
  { label: "Biên bản đánh giá lại TSCĐ", value: "fixed-asset-revaluation" },
];

const sourceTypeLabelMap = sourceTypeOptions.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const toVietnameseStatus = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "POSTED") return "Đã ghi sổ";
  if (value === "REVERSED") return "Đã đảo";
  return "Nháp";
};

const ymdToDmy = (ymd = "") => {
  const [y = "", m = "", d = ""] = String(ymd).split("-");
  if (!d || !m || !y) return "";
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
};

const hasLineData = (line = {}) =>
  Boolean(
    String(line.description || "").trim() ||
      String(line.debitAccount || "").trim() ||
      String(line.creditAccount || "").trim() ||
      roundMoney(line.amount) > 0 ||
      String(line.note || "").trim()
  );

function AccountingVoucherDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [form, setForm] = useState(createInitialForm());
  const [voucherDate, setVoucherDate] = useState(getTodayDateParts());
  const [sourceQuery, setSourceQuery] = useState({
    sourceTypes: "",
    keyword: "",
    fromDate: "",
    toDate: "",
  });
  const [sourceItems, setSourceItems] = useState([]);
  const [selectedSourceKeys, setSelectedSourceKeys] = useState({});

  const readOnly = containView || (id && form.status !== "DRAFT");

  useEffect(() => {
    (async () => {
      try {
        if (!id) {
          const response = await AccountingVouchersService.getInit();
          const initData = response.data ? response.data : response;

          setForm((prev) => ({
            ...prev,
            unit: initData.unit || "",
            companyAddress: initData.companyAddress || "",
            numberingMode: initData.numberingMode || "MONTH",
            status: initData.status || "DRAFT",
          }));
          return;
        }

        const response = await AccountingVouchersService.getById(id);
        const data = response.data ? response.data : response;
        const rows = Array.isArray(data.lines) && data.lines.length ? data.lines : [createLine()];

        setForm({
          unit: data.unit || data.company?.name || "",
          companyAddress: data.companyAddress || data.company?.address || "",
          voucherNumber: data.voucherNumber || "",
          voucherDate: data.voucherDate || "",
          description: data.description || "",
          numberingMode: data.numberingMode || "MONTH",
          attachmentsCount: String(data.attachmentsCount ?? ""),
          preparerName: data.preparerName || "",
          chiefAccountantName: data.chiefAccountantName || "",
          legalRepresentativeName: data.legalRepresentativeName || "",
          signDate: data.signDate || "",
          status: data.status || "DRAFT",
          sourceRefs: data.sourceRefs || [],
          lines: rows.map((row) => ({
            description: row.description || "",
            debitAccount: row.debitAccount || "",
            creditAccount: row.creditAccount || "",
            amount: formatVndIntegerInput(row.amount ?? ""),
            note: row.note || "",
            origin: row.origin || "manual",
            sourceType: row.sourceType || "",
            sourceId: row.sourceId || "",
          })),
        });
        setVoucherDate(splitDateParts(data.voucherDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu chứng từ ghi sổ");
      }
    })();
  }, [id]);

  const rows = form.lines || [];
  const totalAmount = useMemo(() => rows.reduce((sum, row) => sum + roundMoney(row.amount), 0), [rows]);

  const setHeaderField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setDateField = (part, value) => {
    setVoucherDate((prev) => ({
      ...prev,
      [part]: sanitizeDatePart(value, part === "year" ? 4 : 2),
    }));
  };

  const padDatePartOnBlur = (part) => {
    if (part === "year") return;
    setVoucherDate((prev) => {
      const current = String(prev[part] || "");
      if (!current) return prev;
      return { ...prev, [part]: current.padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setForm((prev) => {
      const next = [...(prev.lines || [])];
      if (field === "amount") {
        next[index] = { ...next[index], amount: formatVndIntegerInput(value) };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return { ...prev, lines: next };
    });
  };

  const handleAddRow = () => {
    setForm((prev) => ({ ...prev, lines: [...(prev.lines || []), createLine()] }));
  };

  const handleRemoveRow = (index) => {
    setForm((prev) => {
      const current = prev.lines || [];
      if (current.length <= 1) return prev;
      return { ...prev, lines: current.filter((_, i) => i !== index) };
    });
  };

  const handleLoadSources = async () => {
    try {
      setLoadingSources(true);
      const response = await AccountingVouchersService.getHybridSources({
        ...sourceQuery,
        fromDate: ymdToDmy(sourceQuery.fromDate),
        toDate: ymdToDmy(sourceQuery.toDate),
      });
      const data = response.data ? response.data : response;
      setSourceItems(data.items || []);
    } catch (error) {
      toast.error("Không thể tải danh sách chứng từ gốc");
    } finally {
      setLoadingSources(false);
    }
  };

  const toggleSource = (item) => {
    const key = `${item.sourceType}::${item.sourceId}`;
    setSelectedSourceKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAutoMap = async () => {
    try {
      const selected = sourceItems
        .filter((item) => selectedSourceKeys[`${item.sourceType}::${item.sourceId}`])
        .map((item) => ({ sourceType: item.sourceType, sourceId: item.sourceId }));

      if (!selected.length) {
        toast.error("Vui lòng chọn ít nhất 1 chứng từ gốc để map");
        return;
      }

      const manualRows = (form.lines || []).filter(
        (line) => (line.origin || "manual") === "manual" && hasLineData(line)
      );
      if (manualRows.length) {
        const confirmed = window.confirm(
          "Map tự động sẽ thay toàn bộ dòng auto hiện tại và giữ lại dòng manual. Bạn muốn tiếp tục?"
        );
        if (!confirmed) return;
      }

      setLoadingMap(true);
      const response = await AccountingVouchersService.previewHybrid({ sources: selected });
      const data = response.data ? response.data : response;

      const autoRows = (data.lines || []).map((row) => ({
        description: row.description || "",
        debitAccount: row.debitAccount || "",
        creditAccount: row.creditAccount || "",
        amount: formatVndIntegerInput(row.amount || 0),
        note: row.note || "",
        origin: row.origin || "auto",
        sourceType: row.sourceType || "",
        sourceId: row.sourceId || "",
      }));

      setForm((prev) => ({
        ...prev,
        lines: [...manualRows, ...autoRows],
        sourceRefs: data.sourceRefs || [],
        attachmentsCount: String((data.sourceRefs || []).length || 0),
      }));
      toast.success("Đã map tự động chứng từ gốc vào CTGS");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Map chứng từ gốc thất bại");
    } finally {
      setLoadingMap(false);
    }
  };

  const buildPayload = () => {
    const voucherDateText = validateDateParts(voucherDate, "Ngày chứng từ");

    const lines = (form.lines || [])
      .map((row) => ({
        description: (row.description || "").trim(),
        debitAccount: (row.debitAccount || "").trim(),
        creditAccount: (row.creditAccount || "").trim(),
        amount: roundMoney(row.amount),
        note: (row.note || "").trim(),
        origin: row.origin || "manual",
        sourceType: row.sourceType || "",
        sourceId: row.sourceId || "",
      }))
      .filter((row) => row.description || row.debitAccount || row.creditAccount || row.amount > 0 || row.note);

    if (!lines.length) {
      throw new Error("Vui lòng nhập ít nhất 1 dòng chứng từ");
    }

    return {
      ...form,
      voucherDate: voucherDateText,
      attachmentsCount: Number(String(form.attachmentsCount || "").replace(/\D/g, "") || 0),
      lines,
      sourceRefs: form.sourceRefs || [],
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await AccountingVouchersService.create(payload);
        toast.success("Tạo chứng từ ghi sổ thành công");
      } else {
        await AccountingVouchersService.update(id, payload);
        toast.success("Cập nhật chứng từ ghi sổ thành công");
      }
      navigation("/templates/accounting-vouchers");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu chứng từ ghi sổ thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePost = async () => {
    try {
      if (!id) {
        toast.error("Vui lòng lưu CTGS trước khi ghi sổ");
        return;
      }
      await AccountingVouchersService.postVoucher(id);
      setForm((prev) => ({ ...prev, status: "POSTED" }));
      toast.success("Ghi sổ thành công");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Ghi sổ thất bại");
    }
  };

  const handleReverse = async () => {
    try {
      if (!id) return;
      const reason = window.prompt("Nhập lý do đảo chứng từ:", "Điều chỉnh sai sót nghiệp vụ") || "";
      await AccountingVouchersService.reverseVoucher(id, { reason });
      setForm((prev) => ({ ...prev, status: "REVERSED" }));
      toast.success("Đảo chứng từ thành công");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đảo chứng từ thất bại");
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        unit: form.unit,
        companyAddress: form.companyAddress,
        voucherNumber: form.voucherNumber,
        voucherDate: `${String(voucherDate.day || "").padStart(2, "0")}/${String(voucherDate.month || "").padStart(
          2,
          "0"
        )}/${voucherDate.year || ""}`,
        attachmentsCount: form.attachmentsCount,
      },
      rows,
      totalAmount,
    });

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép pop-up.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 200);
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "18px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "250px",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
      padding: 0,
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
      padding: 0,
    },
    inlineDateInput: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      color: "#000",
      padding: "0 2px",
    },
    inlineDateText: { marginLeft: 6, marginRight: 6, display: "inline-block", minWidth: 110, textAlign: "center" },
    titleBox: { textAlign: "center", margin: "16px 0" },
    titleMain: { fontSize: "20px", fontWeight: "bold", margin: 0, textTransform: "uppercase", lineHeight: 1.1 },
    tableWrap: { overflowX: "auto", border: "1px solid #ccc", marginTop: 8 },
    table: { width: "100%", borderCollapse: "collapse", minWidth: 980, fontSize: "12px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: {
      border: "1px solid #000",
      padding: "0",
      verticalAlign: "middle",
      height: "32px",
    },
    inputText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
      lineHeight: "32px",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
      lineHeight: "32px",
    },
    inputNumber: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
      lineHeight: "32px",
    },
  };

  const sourceControlStyle = { height: 36, boxSizing: "border-box" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={s.headerTop}>
                  <div style={{ width: "60%" }}>
                    <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                      Đơn vị:{" "}
                      <input
                        disabled={readOnly}
                        style={s.inputDotted}
                        value={form.unit}
                        onChange={(e) => setHeaderField("unit", e.target.value)}
                      />
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      Địa chỉ:{" "}
                      <input
                        disabled={readOnly}
                        style={s.inputDotted}
                        value={form.companyAddress}
                        onChange={(e) => setHeaderField("companyAddress", e.target.value)}
                      />
                    </div>
                    <div style={{ marginTop: 8, fontStyle: "italic" }}>
                      Trạng thái: {toVietnameseStatus(form.status)}
                    </div>
                  </div>
                  <div style={{ textAlign: "center", width: "40%" }}>
                    <div style={{ fontWeight: "bold", fontSize: "20px" }}>Mẫu số S02a-DN</div>
                    <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: 4, lineHeight: 1.2 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                {!containView && (
                  <SoftBox mb={2} p={1.5} style={{ border: "1px solid #ddd", borderRadius: 4 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Nguồn chứng từ gốc</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      <select
                        disabled={readOnly}
                        value={sourceQuery.sourceTypes}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, sourceTypes: e.target.value }))}
                        style={{ ...sourceControlStyle, minWidth: 210 }}
                      >
                        <option value="">Tất cả loại chứng từ</option>
                        {sourceTypeOptions.map((item) => (
                          <option value={item.value} key={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <input
                        disabled={readOnly}
                        placeholder="Tìm theo số/diễn giải"
                        style={{ ...sourceControlStyle, minWidth: 220, padding: "0 8px" }}
                        value={sourceQuery.keyword}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, keyword: e.target.value }))}
                      />
                      <input
                        disabled={readOnly}
                        type="date"
                        style={{ ...sourceControlStyle, width: 170, padding: "0 8px" }}
                        value={sourceQuery.fromDate}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, fromDate: e.target.value }))}
                      />
                      <input
                        disabled={readOnly}
                        type="date"
                        style={{ ...sourceControlStyle, width: 170, padding: "0 8px" }}
                        value={sourceQuery.toDate}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, toDate: e.target.value }))}
                      />
                      <SoftButton
                        disabled={readOnly || loadingSources}
                        variant="outlined"
                        color="dark"
                        size="small"
                        style={{ ...sourceControlStyle, padding: "0 14px" }}
                        onClick={handleLoadSources}
                      >
                        {loadingSources ? "Đang tải..." : "Tải chứng từ gốc"}
                      </SoftButton>
                      <SoftButton
                        disabled={readOnly || loadingMap}
                        variant="gradient"
                        color="info"
                        size="small"
                        style={{ ...sourceControlStyle, padding: "0 14px" }}
                        onClick={handleAutoMap}
                      >
                        {loadingMap ? "Đang map..." : "Map tự động"}
                      </SoftButton>
                    </div>

                    {!!sourceItems.length && (
                      <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid #ececec" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={s.th}>Chọn</th>
                              <th style={s.th}>Loại</th>
                              <th style={s.th}>Số</th>
                              <th style={s.th}>Ngày</th>
                              <th style={s.th}>Diễn giải</th>
                              <th style={s.th}>Nợ</th>
                              <th style={s.th}>Có</th>
                              <th style={s.th}>Tiền</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sourceItems.map((item) => {
                              const key = `${item.sourceType}::${item.sourceId}`;
                              return (
                                <tr key={key}>
                                  <td style={{ ...s.td, textAlign: "center" }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(selectedSourceKeys[key])}
                                      onChange={() => toggleSource(item)}
                                      disabled={readOnly}
                                    />
                                  </td>
                                  <td style={{ ...s.td, padding: "0 6px" }}>
                                    {sourceTypeLabelMap[item.sourceType] || item.sourceType}
                                  </td>
                                  <td style={{ ...s.td, padding: "0 6px" }}>{item.sourceNumber}</td>
                                  <td style={{ ...s.td, padding: "0 6px", textAlign: "center" }}>{item.sourceDate}</td>
                                  <td style={{ ...s.td, padding: "0 6px" }}>{item.description}</td>
                                  <td style={{ ...s.td, padding: "0 6px", textAlign: "center" }}>{item.debitAccount}</td>
                                  <td style={{ ...s.td, padding: "0 6px", textAlign: "center" }}>{item.creditAccount}</td>
                                  <td style={{ ...s.td, padding: "0 6px", textAlign: "right" }}>{fmt(item.amount)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </SoftBox>
                )}

                <div style={s.titleBox}>
                  <h2 style={s.titleMain}>CHỨNG TỪ GHI SỔ</h2>
                  <div style={{ fontSize: "14px", fontWeight: "bold", marginTop: 4 }}>
                    Số:
                    <input
                      disabled={readOnly}
                      style={{ ...s.inputDottedSmall, width: 120, marginLeft: 6 }}
                      value={form.voucherNumber}
                      onChange={(e) => setHeaderField("voucherNumber", e.target.value)}
                    />
                  </div>

                  <div style={{ fontStyle: "italic", marginTop: 8, fontSize: "14px" }}>
                    Ngày{" "}
                    {readOnly ? (
                      <span style={s.inlineDateText}>{String(voucherDate.day || "").padStart(2, "0")}</span>
                    ) : (
                      <input
                        disabled={readOnly}
                        style={{ ...s.inlineDateInput, width: 34 }}
                        value={voucherDate.day}
                        onChange={(e) => setDateField("day", e.target.value)}
                        onBlur={() => padDatePartOnBlur("day")}
                      />
                    )}{" "}
                    tháng{" "}
                    {readOnly ? (
                      <span style={s.inlineDateText}>{String(voucherDate.month || "").padStart(2, "0")}</span>
                    ) : (
                      <input
                        disabled={readOnly}
                        style={{ ...s.inlineDateInput, width: 34 }}
                        value={voucherDate.month}
                        onChange={(e) => setDateField("month", e.target.value)}
                        onBlur={() => padDatePartOnBlur("month")}
                      />
                    )}{" "}
                    năm{" "}
                    {readOnly ? (
                      <span style={s.inlineDateText}>{voucherDate.year || ""}</span>
                    ) : (
                      <input
                        disabled={readOnly}
                        style={{ ...s.inlineDateInput, width: 56 }}
                        value={voucherDate.year}
                        onChange={(e) => setDateField("year", e.target.value)}
                      />
                    )}
                  </div>
                </div>

                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={s.th}>
                          Trích yếu
                        </th>
                        <th colSpan={2} style={s.th}>
                          Số hiệu tài khoản
                        </th>
                        <th rowSpan={2} style={s.th}>
                          Số tiền
                        </th>
                        <th rowSpan={2} style={s.th}>
                          Ghi chú
                        </th>
                        {!readOnly && (
                          <th rowSpan={2} style={{ ...s.th, width: "30px" }}>
                            #
                          </th>
                        )}
                      </tr>
                      <tr>
                        <th style={s.th}>Nợ</th>
                        <th style={s.th}>Có</th>
                      </tr>
                      <tr style={{ backgroundColor: "#fafafa" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>D</th>
                        {!readOnly && <th style={s.th}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                          <td style={s.td}>
                            <input
                              disabled={readOnly}
                              style={s.inputText}
                              value={row.description}
                              onChange={(e) => setRowField(index, "description", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={readOnly}
                              style={s.inputCenter}
                              value={row.debitAccount}
                              onChange={(e) => setRowField(index, "debitAccount", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={readOnly}
                              style={s.inputCenter}
                              value={row.creditAccount}
                              onChange={(e) => setRowField(index, "creditAccount", e.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            {readOnly ? (
                              <span style={s.inputNumber}>{fmt(row.amount)}</span>
                            ) : (
                              <input
                                disabled={readOnly}
                                type="text"
                                inputMode="numeric"
                                style={s.inputNumber}
                                value={row.amount}
                                onChange={(e) => setRowField(index, "amount", e.target.value)}
                              />
                            )}
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={readOnly}
                              style={s.inputText}
                              value={row.note}
                              onChange={(e) => setRowField(index, "note", e.target.value)}
                            />
                          </td>
                          {!readOnly && (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(index)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "red",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                x
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        <td style={{ ...s.td, textAlign: "right", paddingRight: 6, fontWeight: 700 }}>
                          {fmt(totalAmount)}
                        </td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        {!readOnly && <td style={s.td}></td>}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {!!form.sourceRefs?.length && (
                  <SoftBox mt={1} fontSize={12}>
                    Nguồn chứng từ đã liên kết:{" "}
                    {form.sourceRefs
                      .map((item) => `${sourceTypeLabelMap[item.sourceType] || item.sourceType}:${item.sourceNumber}`)
                      .join("; ")}
                  </SoftBox>
                )}

                <SoftBox mt={2}>
                  Kèm theo{" "}
                  <input
                    disabled={readOnly}
                    style={{ ...s.inputDottedSmall, width: 80 }}
                    value={form.attachmentsCount}
                    onChange={(e) => setHeaderField("attachmentsCount", e.target.value.replace(/\D/g, ""))}
                  />{" "}
                  chứng từ gốc
                </SoftBox>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {!readOnly && (
                      <SoftButton variant="outlined" color="dark" onClick={handleAddRow}>
                        THÊM DÒNG
                      </SoftButton>
                    )}

                    {!containView && !readOnly && (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}

                    {id && form.status === "DRAFT" && (
                      <SoftButton variant="gradient" color="success" onClick={handlePost}>
                        Ghi sổ
                      </SoftButton>
                    )}

                    {id && form.status === "POSTED" && (
                      <SoftButton variant="outlined" color="warning" onClick={handleReverse}>
                        Đảo chứng từ
                      </SoftButton>
                    )}

                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingVoucherDetail;
